import { useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import type { Deal } from '../../types';
import { bucketSolution } from './OfferingChart';
function generateColors(count: number): string[] {
  const goldenAngle = 137.508;
  return Array.from({
    length: count
  }, (_, i) => {
    const hue = i * goldenAngle % 360;
    return `hsl(${Math.round(hue)}, 65%, 50%)`;
  });
}
interface Props {
  deals: Deal[];
  top: number;
  onTopChange: (v: number) => void;
  onTotalChange?: (v: number) => void;
  chartHeight?: number;
}
export function AccountBySolutionChart({
  deals,
  top,
  onTotalChange,
  chartHeight = 220
}: Props) {
  const solAccounts: Record<string, Set<string>> = {};
  const solSeenIds: Record<string, Set<string>> = {};
  deals.forEach(d => {
    const sol = bucketSolution(d.products || '');
    if (!solAccounts[sol]) solAccounts[sol] = new Set();
    if (!solSeenIds[sol]) solSeenIds[sol] = new Set();
    if (solSeenIds[sol].has(d.id)) return;
    solSeenIds[sol].add(d.id);
    if (d.account) solAccounts[sol].add(d.account);
  });
  const solSorted = Object.entries(solAccounts).map(([sol, accounts]) => [sol, accounts.size] as [string, number]).sort((a, b) => b[1] - a[1]);
  const totalBuckets = solSorted.length;
  useEffect(() => {
    onTotalChange?.(totalBuckets);
  }, [totalBuckets, onTotalChange]);
  let labels: string[], vals: number[];
  if (totalBuckets > top) {
    const topSlices = solSorted.slice(0, top);
    const othersCount = solSorted.slice(top).reduce((s, [, v]) => s + v, 0);
    labels = [...topSlices.map(([l]) => l), 'Others'];
    vals = [...topSlices.map(([, v]) => v), othersCount];
  } else {
    labels = solSorted.map(([l]) => l);
    vals = solSorted.map(([, v]) => v);
  }
  const data = labels.map((name, i) => ({
    name,
    value: vals[i]
  }));
  const colors = generateColors(data.length);
  const mid = Math.ceil(data.length / 2);
  const col1 = data.slice(0, mid);
  const col2 = data.slice(mid);
  const renderLegendCol = (items: typeof data) => <ul style={{
    listStyle: 'none',
    padding: 0,
    margin: 0
  }} data-id="element-78" data-mp="2p5anoekgoj3e">
      {items.map((item, i) => {
      const colorIdx = data.indexOf(item);
      return <li key={i} style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        marginBottom: 2,
        fontSize: 9,
        color: '#374151'
      }} data-id="element-79" data-mp="2f61uvp1c1aim" data-mpi={i}>
            <span style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: colors[colorIdx],
          flexShrink: 0
        }} data-id="element-80" data-mp="37tfn59u35hwe" />
            <span data-id="element-81" data-mp="3crh51rfrzb6a">{item.name} ({item.value})</span>
          </li>;
    })}
    </ul>;
  return <div style={{
    display: 'flex',
    alignItems: 'center'
  }} data-id="element-82" data-mp="2y3csl57bfvvy">
      <ResponsiveContainer width="50%" height={chartHeight} data-id="element-83" data-mp="332flxsdxodo3">
        <PieChart data-id="element-84" data-mp="330w9ydzb8os4">
          <Pie data={data} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" strokeWidth={2} stroke="#fff" data-id="element-85" data-mp="3qstj1xbdl2pj">
            {data.map((_, i) => <Cell key={i} fill={colors[i]} data-id="element-86" data-mp="1k26ndcedmg98" data-mpi={i} />)}
          </Pie>
          <Tooltip contentStyle={{
          fontSize: 11,
          border: '1px solid #e8eaf0',
          borderRadius: 6
        }} formatter={(v: number) => [v, 'Accounts']} data-id="element-87" data-mp="1f4etwdy32m2x" />
        </PieChart>
      </ResponsiveContainer>
      <div style={{
      display: 'flex',
      gap: 12,
      flex: 1,
      paddingLeft: 4
    }} data-id="element-88" data-mp="1ox46jc5de5zu">
        {renderLegendCol(col1)}
        {col2.length > 0 && renderLegendCol(col2)}
      </div>
    </div>;
}